import React, { useState, useEffect, useMemo } from "react";
import { C } from "./reportingStyles";

export const GradientDefs = () => (
  <defs>
    <linearGradient id="gCoral" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#FF6B6B" stopOpacity={0.8} />
      <stop offset="100%" stopColor="#009689" stopOpacity={0.15} />
    </linearGradient>
    <linearGradient id="gBlue" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#667EEA" stopOpacity={0.8} />
      <stop offset="100%" stopColor="#764BA2" stopOpacity={0.15} />
    </linearGradient>
    <linearGradient id="gGreen" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#43E97B" stopOpacity={0.8} />
      <stop offset="100%" stopColor="#38F9D7" stopOpacity={0.15} />
    </linearGradient>
    <linearGradient id="gOrange" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#F7971E" stopOpacity={0.8} />
      <stop offset="100%" stopColor="#FFD200" stopOpacity={0.15} />
    </linearGradient>
    <linearGradient id="gCoralBar" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stopColor="#009689" />
      <stop offset="100%" stopColor="#00bfa5" />
    </linearGradient>
    <linearGradient id="gBlueBar" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stopColor="#667EEA" />
      <stop offset="100%" stopColor="#A8B8FF" />
    </linearGradient>
  </defs>
);

export const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "rgba(30,41,59,0.92)", backdropFilter: "blur(8px)", borderRadius: 10, padding: "10px 14px", border: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
      <div style={{ fontWeight: 700, color: "#fff", fontSize: 12, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color === "url(#gCoral)" || p.color === "url(#gCoralBar)" ? "#00bfa5" : p.color === "url(#gBlue)" || p.color === "url(#gBlueBar)" ? "#A8B8FF" : p.stroke || p.color, fontSize: 12, fontWeight: 600 }}>
          {p.name}: {p.value}
        </div>
      ))}
    </div>
  );
};

export const Tile = ({ label, value, sub, gradient, icon, delay = 0 }) => (
  <div className="rpt-lift rpt-fade" style={{ background: C.white, borderRadius: 14, padding: "22px 24px", boxShadow: C.shadow, border: `1px solid ${C.border}`, position: "relative", overflow: "hidden", animationDelay: `${delay}ms` }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: gradient || `linear-gradient(90deg, ${C.coral}, #00bfa5)` }} />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: C.text, letterSpacing: -1, lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: C.textMed, marginTop: 8 }}>{sub}</div>}
      </div>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: gradient || `linear-gradient(135deg, ${C.coral}, #00bfa5)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)" }}>{icon}</div>
    </div>
  </div>
);

export const RCard = ({ title, children, style: s, className = "" }) => (
  <div className={`rpt-fade ${className}`} style={{ background: C.white, borderRadius: 14, padding: "22px 24px", boxShadow: C.shadow, border: `1px solid ${C.border}`, marginBottom: 20, ...s }}>
    {title && <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>{title}</div>}
    {children}
  </div>
);

// Heading for a form section inside an RCard; `right` holds an optional counter or action.
export const SectionTitle = ({ children, right }) => (
  <div
    style={{
      fontSize: 14,
      fontWeight: 700,
      color: C.text,
      marginBottom: 16,
      paddingBottom: 10,
      borderBottom: `2px solid ${C.borderLight}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    }}
  >
    <span>{children}</span>
    {right}
  </div>
);

export const Badge = ({ children, color, bg }) => (
  <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, color, background: bg, letterSpacing: 0.3 }}>{children}</span>
);

export const StatusBadge = ({ s }) => (
  <Badge color={s === "Active" ? C.green : s === "Redeemed" ? C.orange : C.coral} bg={s === "Active" ? C.greenLight : s === "Redeemed" ? C.orangeLight : C.coralLight}>{s}</Badge>
);

export const EventBadge = ({ t }) => {
  const s = t || "";
  return <Badge color={s.includes("Earned") ? C.green : s.includes("Used") || s.includes("Redeemed") ? C.orange : C.coral} bg={s.includes("Earned") ? C.greenLight : s.includes("Used") || s.includes("Redeemed") ? C.orangeLight : C.coralLight}>{s}</Badge>;
};

export const ChannelBadge = ({ c }) => (
  <Badge color={c === "app" ? C.blue : C.orange} bg={c === "app" ? C.blueLight : C.orangeLight}>{c?.toUpperCase()}</Badge>
);

export const Loader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 60 }}>
    <div style={{ width: 36, height: 36, border: `3px solid ${C.borderLight}`, borderTopColor: C.coral, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ─── Pagination ──────────────────────────────────────────
// Client-side pagination for tables whose full dataset is already loaded. Lifted here
// from CustomerDetailPage so every reporting-styled table shares one implementation.
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

export const TablePagination = ({ total, page, limit, onPageChange, onLimitChange, pageSizeOptions = PAGE_SIZE_OPTIONS }) => {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  const pageBtn = (p) => (
    <button key={p} onClick={() => onPageChange(p)}
      style={{
        width: 30, height: 30, borderRadius: 8,
        border: p === page ? `1.5px solid ${C.coral}` : `1px solid ${C.border}`,
        background: p === page ? C.coralLight : C.white, color: p === page ? C.coral : C.text,
        cursor: "pointer", fontSize: 12, fontWeight: p === page ? 700 : 500,
        transition: "all 0.15s", fontFamily: "inherit",
      }}
    >{p}</button>
  );

  const arrowBtn = (label, target, disabled) => (
    <button onClick={() => onPageChange(target)} disabled={disabled}
      style={{
        width: 30, height: 30, borderRadius: 8, border: `1px solid ${C.border}`,
        background: disabled ? C.bg : C.white, cursor: disabled ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, color: disabled ? C.textLight : C.text, transition: "all 0.15s", fontFamily: "inherit",
      }}
    >{label}</button>
  );

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", fontSize: 12, color: C.textMed, flexWrap: "wrap", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span>Show</span>
        <select
          value={limit}
          onChange={(e) => { onLimitChange(Number(e.target.value)); onPageChange(1); }}
          style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, fontFamily: "inherit", outline: "none", cursor: "pointer", background: C.white }}
        >
          {pageSizeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span>per page</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ marginRight: 8 }}>{from}–{to} of {total}</span>
        {arrowBtn("‹", page - 1, page <= 1)}
        {totalPages <= 5
          ? Array.from({ length: totalPages }, (_, i) => i + 1).map(pageBtn)
          : [
              1,
              page > 3 ? "..." : null,
              page > 2 ? page - 1 : null,
              page > 1 && page < totalPages ? page : null,
              page < totalPages - 1 ? page + 1 : null,
              page < totalPages - 2 ? "..." : null,
              totalPages,
            ]
              .filter((v, i, a) => v !== null && (typeof v === "string" || a.indexOf(v) === i))
              .map((p, i) =>
                typeof p === "string"
                  ? <span key={`dots-${i}`} style={{ padding: "0 2px" }}>...</span>
                  : pageBtn(p)
              )}
        {arrowBtn("›", page + 1, page >= totalPages)}
      </div>
    </div>
  );
};

// Slices an in-memory array into pages. Resets to page 1 whenever the dataset size or
// page size changes — otherwise filtering a list down can strand you on an empty page.
export const usePagination = (data, defaultLimit = 5) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);

  useEffect(() => { setPage(1); }, [data.length, limit]);

  const paged = useMemo(() => {
    const start = (page - 1) * limit;
    return data.slice(start, start + limit);
  }, [data, page, limit]);

  return { paged, page, limit, setPage, setLimit, total: data.length };
};
